// src/infra/api/ColorConversionApi.js
import axios from 'axios';
import { buildApiConfig } from '../../config/sensorColorConfig';

const COLOR_API_BASE_URL = 'https://spectral.bioailab.com.br';
// const COLOR_API_BASE_URL = 'http://localhost:8000';

/**
 * Converte dados raw do sensor para espaços de cor usando a API externa
 * @param {Object} payload - Dados do sensor (formato nested)
 * @param {string} payload.sensorType - Tipo do sensor (APDS9960 ou AS7341)
 * @param {Object} payload.rawData - Dados raw do sensor (pode conter arrays ou valores únicos)
 * @param {Array<string>} payload.targetColorSpaces - Espaços de cor desejados
 * @param {Object} payload.calibration - Sub-objeto de calibração (type, mode, referenceReading)
 * @param {Object} [payload.series] - Sub-objeto de série temporal (startIndex, smoothing)
 * @param {Object} payload.output - Sub-objeto de saída (chromaticAdaptation, autoExposure, etc.)
 * @returns {Promise<Object>} Resposta da API com as conversões de cor
 */
export async function convertSensorDataToColor(payload) {
    console.log('[ColorConversionApi] Enviando payload para API:', {
        sensorType: payload.sensorType,
        rawDataKeys: Object.keys(payload.rawData),
        rawDataLengths: Object.entries(payload.rawData).map(
            ([k, v]) => `${k}: ${Array.isArray(v) ? v.length : 'single'}`
        ),
        targetColorSpaces: payload.targetColorSpaces,
        calibration: payload.calibration,
        series: payload.series,
        output: payload.output,
    });

    try {
        const response = await axios.post(
            `${COLOR_API_BASE_URL}/convert`,
            payload,
            {
                timeout: 30000, // 30 segundos
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        );

        // Normaliza response: flatten results.colorSpaces para results.{space}
        // para manter compatibilidade com consumers existentes
        const data = response.data;
        if (data?.results?.colorSpaces) {
            Object.assign(data.results, data.results.colorSpaces);
        }

        console.log('[ColorConversionApi] Resposta da API:', {
            hasResults: !!data?.results,
            colorSpaces: data?.results?.colorSpaces
                ? Object.keys(data.results.colorSpaces)
                : [],
            srgbCount: data?.results?.sRGB
                ? Array.isArray(data.results.sRGB)
                    ? data.results.sRGB.length
                    : 1
                : 0,
        });

        return data;
    } catch (error) {
        console.error('[ColorConversionApi] Erro na conversão:', error);
        if (error.code === 'ECONNREFUSED') {
            throw new Error(
                'API de conversão de cores não disponível. Verifique se o servidor está rodando em http://localhost:8000'
            );
        }
        throw new Error(
            error.response?.data?.message ||
                (Array.isArray(error.response?.data?.detail)
                    ? error.response.data.detail.map((d) => d.msg).join('; ')
                    : error.response?.data?.detail) ||
                error.message ||
                'Erro ao converter dados do sensor'
        );
    }
}

/**
 * Verifica se a API de conversão de cores está disponível
 * @returns {Promise<boolean>}
 */
export async function isColorApiAvailable() {
    try {
        await axios.get(`${COLOR_API_BASE_URL}/`, { timeout: 3000 });
        return true;
    } catch (error) {
        return false;
    }
}

/**
 * Subamostra um array mantendo pontos igualmente espaçados
 * @param {Array} data - Array de dados original
 * @param {number} targetSize - Número desejado de pontos
 * @returns {Array} Array subamostrado
 */
function subsampleArray(data, targetSize = 50) {
    if (!data || data.length <= targetSize) {
        return data;
    }

    const result = [];
    const step = (data.length - 1) / (targetSize - 1);

    for (let i = 0; i < targetSize; i++) {
        const index = Math.round(i * step);
        result.push(data[index]);
    }

    return result;
}

/**
 * Extrai dados do sensor do gráfico spectral
 * @param {Object} chart - Dados do gráfico
 * @param {string} sensorType - Tipo do sensor (APDS9960 ou AS7341)
 * @param {boolean} isFluorescence - Se true, identifica modo fluorescência (não altera clear)
 * @param {boolean} isNephelometry - Se true, força nir = 0 (nefelometria não usa infravermelho)
 * @param {boolean} useSubsampling - Se true, subamostra para 50 pontos; se false, usa todos os dados
 * @returns {Object} Dados formatados para a API
 */
export function extractSensorDataFromChart(
    chart,
    sensorType,
    isFluorescence = false,
    isNephelometry = false,
    useSubsampling = true
) {
    if (!chart || !chart.series) {
        throw new Error('Dados do gráfico inválidos');
    }

    const seriesList = Object.values(chart.series)[0] || [];

    console.log('[ColorConversionApi] Sensor:', sensorType);
    console.log(
        '[ColorConversionApi] Séries disponíveis:',
        seriesList.map((s) => s.name)
    );

    if (sensorType === 'APDS9960') {
        // Para APDS9960, os canais são .red, .green, .blue, .clear
        const rSeries = seriesList.find((s) => {
            const name = s.name.toLowerCase();
            return (
                name.endsWith('.red') || name.endsWith('red') || name === 'r'
            );
        });
        const gSeries = seriesList.find((s) => {
            const name = s.name.toLowerCase();
            return (
                name.endsWith('.green') ||
                name.endsWith('green') ||
                name === 'g'
            );
        });
        const bSeries = seriesList.find((s) => {
            const name = s.name.toLowerCase();
            return (
                name.endsWith('.blue') || name.endsWith('blue') || name === 'b'
            );
        });
        const cSeries = seriesList.find((s) => {
            const name = s.name.toLowerCase();
            return (
                name.endsWith('.clear') ||
                name.endsWith('clear') ||
                name === 'c'
            );
        });

        console.log('[ColorConversionApi] APDS9960 - Séries encontradas:', {
            r: rSeries?.name,
            g: gSeries?.name,
            b: bSeries?.name,
            c: cSeries?.name,
        });

        if (!rSeries || !gSeries || !bSeries || !cSeries) {
            console.error(
                '[ColorConversionApi] Dados do sensor APDS9960 incompletos!'
            );
            throw new Error(
                'Dados do sensor APDS9960 incompletos. Séries necessárias: .red, .green, .blue, .clear'
            );
        }

        // Filtra valores nulos e remove último ponto (outlier)
        const rFiltered = rSeries.data.filter(
            (v) => v !== null && v !== undefined
        );
        const gFiltered = gSeries.data.filter(
            (v) => v !== null && v !== undefined
        );
        const bFiltered = bSeries.data.filter(
            (v) => v !== null && v !== undefined
        );
        const cFiltered = cSeries.data.filter(
            (v) => v !== null && v !== undefined
        );

        // Remove último ponto de cada série (outlier de remoção do tubo)
        const rWithoutLast = rFiltered.slice(0, -1);
        const gWithoutLast = gFiltered.slice(0, -1);
        const bWithoutLast = bFiltered.slice(0, -1);
        const cWithoutLast = cFiltered.slice(0, -1);

        // Subamostra se solicitado
        const rData = useSubsampling
            ? subsampleArray(rWithoutLast, 50)
            : rWithoutLast;
        const gData = useSubsampling
            ? subsampleArray(gWithoutLast, 50)
            : gWithoutLast;
        const bData = useSubsampling
            ? subsampleArray(bWithoutLast, 50)
            : bWithoutLast;
        const cData = useSubsampling
            ? subsampleArray(cWithoutLast, 50)
            : cWithoutLast;

        console.log(
            `[ColorConversionApi] APDS9960 - ${useSubsampling ? 'Subamostrado' : 'Todos os dados'} (último ponto removido):`,
            {
                r: `${rSeries.data.length} → ${rWithoutLast.length} → ${rData.length} pontos`,
                g: `${gSeries.data.length} → ${gWithoutLast.length} → ${gData.length} pontos`,
                b: `${bSeries.data.length} → ${bWithoutLast.length} → ${bData.length} pontos`,
                c: `${cSeries.data.length} → ${cWithoutLast.length} → ${cData.length} pontos`,
            }
        );

        // Tenta extrair gain e integrationTime das séries do gráfico
        const gainSeriesApds = seriesList.find((s) => {
            const name = s.name.toLowerCase();
            return name.endsWith('.gain') || name === 'gain';
        });
        const intTimeSeriesApds = seriesList.find((s) => {
            const name = s.name.toLowerCase();
            return (
                name.endsWith('.integrationtime') ||
                name.endsWith('.timems') ||
                name === 'integrationtime' ||
                name === 'timems'
            );
        });

        const gainValApds =
            gainSeriesApds?.data?.find((v) => v != null) ?? null;
        const intTimeValApds =
            intTimeSeriesApds?.data?.find((v) => v != null) ?? null;

        console.log(
            '[ColorConversionApi] APDS9960 - gain/integrationTime do gráfico:',
            { gain: gainValApds, integrationTime: intTimeValApds }
        );

        return {
            r: rData,
            g: gData,
            b: bData,
            c: cData,
            gain: gainValApds,
            integrationTime: intTimeValApds,
        };
    } else if (sensorType === 'AS7341') {
        // Para AS7341, precisamos de f1-f8 + clear + nir (10 canais no total)
        const requiredChannels = [
            'f1',
            'f2',
            'f3',
            'f4',
            'f5',
            'f6',
            'f7',
            'f8',
        ];
        const optionalChannels = ['clear', 'nir'];
        const channelData = {};
        const channelsFound = {};

        // Busca canais obrigatórios
        for (const channel of requiredChannels) {
            const series = seriesList.find((s) => {
                const name = s.name.toLowerCase();
                return (
                    name.endsWith(`.${channel}`) ||
                    name.endsWith(channel) ||
                    name === channel
                );
            });

            if (!series) {
                console.error(
                    `[ColorConversionApi] Canal ${channel} não encontrado. Séries disponíveis:`,
                    seriesList.map((s) => s.name)
                );
                throw new Error(
                    `Dados do sensor AS7341 incompletos: canal ${channel} não encontrado`
                );
            }

            // Filtra valores nulos e remove último ponto (outlier)
            const filteredData = series.data.filter(
                (v) => v !== null && v !== undefined
            );
            const dataWithoutLast = filteredData.slice(0, -1); // Remove último ponto

            // Subamostra se solicitado
            channelData[channel] = useSubsampling
                ? subsampleArray(dataWithoutLast, 50)
                : dataWithoutLast;
            channelsFound[channel] =
                `${series.name} (${series.data.length} → ${dataWithoutLast.length} → ${channelData[channel].length} pontos)`;
        }

        // Busca canais opcionais (clear e nir)
        for (const channel of optionalChannels) {
            const series = seriesList.find((s) => {
                const name = s.name.toLowerCase();
                return (
                    name.endsWith(`.${channel}`) ||
                    name.endsWith(channel) ||
                    name === channel
                );
            });

            if (series) {
                const filteredData = series.data.filter(
                    (v) => v !== null && v !== undefined
                );
                const dataWithoutLast = filteredData.slice(0, -1); // Remove último ponto

                // Subamostra se solicitado
                channelData[channel] = useSubsampling
                    ? subsampleArray(dataWithoutLast, 50)
                    : dataWithoutLast;
                channelsFound[channel] =
                    `${series.name} (${series.data.length} → ${dataWithoutLast.length} → ${channelData[channel].length} pontos)`;
            } else {
                // Se não encontrou, usa array de zeros do mesmo tamanho que f1
                const dataLength = channelData.f1.length;
                channelData[channel] = new Array(dataLength).fill(0);
                channelsFound[channel] =
                    `${channel} (padrão: 0, ${dataLength} pontos)`;
                console.warn(
                    `[ColorConversionApi] Canal ${channel} não encontrado - usando valores padrão (0)`
                );
            }
        }

        console.log(
            '[ColorConversionApi] AS7341 - Canais encontrados:',
            channelsFound
        );

        // Tenta extrair gain e integrationTime das séries do gráfico
        const gainSeries = seriesList.find((s) => {
            const name = s.name.toLowerCase();
            return name.endsWith('.gain') || name === 'gain';
        });
        const intTimeSeries = seriesList.find((s) => {
            const name = s.name.toLowerCase();
            return (
                name.endsWith('.integrationtime') ||
                name.endsWith('.timems') ||
                name === 'integrationtime' ||
                name === 'timems'
            );
        });

        const gainVal = gainSeries?.data?.find((v) => v != null) ?? null;
        const intTimeVal = intTimeSeries?.data?.find((v) => v != null) ?? null;

        console.log(
            '[ColorConversionApi] AS7341 - gain/integrationTime do gráfico:',
            { gain: gainVal, integrationTime: intTimeVal }
        );

        return {
            ...channelData,
            gain: gainVal,
            integrationTime: intTimeVal,
        };
    }

    throw new Error(`Tipo de sensor não suportado: ${sensorType}`);
}

/**
 * Extrai a primeira leitura (referência) de um chart de turbidimetria
 * Útil para usar como referência em nefelometria (mesmo emissor)
 * @param {Object} chart - Dados do gráfico de turbidimetria
 * @param {string} sensorType - Tipo do sensor (APDS9960 ou AS7341)
 * @param {boolean} useSubsampling - Se true, subamostra para 50 pontos
 * @param {Object} experiment - Objeto do experimento (opcional). Usado para preencher gain/integrationTime da referência.
 * @returns {Object} Primeira leitura do sensor (referência)
 */
export function extractTurbidimetryReference(
    chart,
    sensorType,
    useSubsampling = true,
    experiment = null
) {
    console.log(
        '[ColorConversionApi] 💧 Extraindo referência de turbidimetria...'
    );

    // Extrai dados de turbidimetria (sem flags de fluorescence/nephelometry)
    const rawData = extractSensorDataFromChart(
        chart,
        sensorType,
        false,
        false,
        useSubsampling
    );

    // Pega a primeira leitura de cada canal
    const reference = {};
    for (const key in rawData) {
        if (Array.isArray(rawData[key]) && rawData[key].length > 0) {
            reference[key] = rawData[key][0];
        } else {
            reference[key] = rawData[key];
        }
    }

    // Preenche gain/integrationTime a partir do experiment.spectral.turbidimetry.reference
    // quando não encontrados no gráfico (esses valores são da calibração)
    const expRef = experiment?.spectral?.turbidimetry?.reference;
    if (reference.gain == null && expRef?.gain != null) {
        reference.gain = expRef.gain;
    }
    if (reference.integrationTime == null) {
        const it = expRef?.integrationTime ?? expRef?.timeMs;
        if (it != null) reference.integrationTime = it;
    }

    console.log(
        '[ColorConversionApi] ✅ Referência de turbidimetria extraída:',
        reference
    );
    return reference;
}

/**
 * Prepara o payload completo para a API de conversão.
 * A configuração base vem de sensorColorConfig.js, mas pode ser
 * sobrescrita via `configOverrides` (ex: toggles do usuário na UI).
 *
 * Resolução de referência (em ordem de prioridade):
 *   1. customReferenceReading (passada externamente)
 *   2. experiment.spectral[refKey].reference (nef/fluo usam turbidimetry)
 *   3. Nefelometria: extrai do turbidimetryChart ou usa referência escura (zeros)
 *   4. Amostra do rawData no índice customReferenceIndex (turbidimetry/fluorescence)
 *   → Se nenhuma disponível, lança erro.
 */
export function prepareColorConversionPayload({
    chart,
    sensorType,
    isFluorescence = false,
    isNephelometry = false,
    useSubsampling = true,
    applyMovingAverage,
    movingAverageWindow,
    measurementType = 'turbidimetry',
    customReferenceReading = null,
    customReferenceIndex = 0,
    startIndex = null,
    experiment = null,
    configOverrides = {},
    turbidimetryChart = null,
}) {
    const overrides = {
        ...(applyMovingAverage != null ? { applyMovingAverage } : {}),
        ...(movingAverageWindow != null ? { movingAverageWindow } : {}),
        ...(startIndex != null ? { startIndex } : {}),
        ...configOverrides,
    };
    const apiConfig = buildApiConfig(measurementType, overrides);

    const rawData = extractSensorDataFromChart(
        chart,
        sensorType,
        isFluorescence,
        isNephelometry,
        useSubsampling
    );

    const gainFromChart = rawData.gain != null;
    const intTimeFromChart = rawData.integrationTime != null;

    // --- Resolução de referência ---
    let referenceReading;

    // 1) Referência customizada externa (maior prioridade)
    if (customReferenceReading) {
        referenceReading = { ...customReferenceReading };

        // 2) Referência do experiment
        //    - Nefelometria usa referência de TURBIDIMETRIA (iluminante branco), NÃO a própria (escuro ~zero)
        //    - Turbidimetria e Fluorescência usam sua própria referência
    } else if (experiment?.spectral) {
        const refKey =
            measurementType === 'nephelometry'
                ? 'turbidimetry'
                : measurementType;
        const expRef = experiment.spectral[refKey]?.reference;
        if (expRef) {
            referenceReading = { ...expRef };
        }
    }

    // 3) Fallback: amostra do rawData no índice customReferenceIndex
    if (!referenceReading) {
        if (isNephelometry) {
            // Nefelometria usa sua própria referência (primeira leitura do rawData)
            const hasArrayData = Object.values(rawData).some(
                (v) => Array.isArray(v) && v.length > 0
            );
            if (!hasArrayData) {
                throw new Error(
                    `Sem referência disponível para ${sensorType}/nephelometry. ` +
                        'Forneça um experiment com referência de calibração ou uma referência customizada.'
                );
            }
            referenceReading = {};
            for (const key in rawData) {
                if (Array.isArray(rawData[key]) && rawData[key].length > 0) {
                    const index = Math.min(
                        customReferenceIndex,
                        rawData[key].length - 1
                    );
                    referenceReading[key] = rawData[key][index];
                } else {
                    referenceReading[key] = rawData[key];
                }
            }
        } else {
            const hasArrayData = Object.values(rawData).some(
                (v) => Array.isArray(v) && v.length > 0
            );
            if (!hasArrayData) {
                throw new Error(
                    `Sem referência disponível para ${sensorType}/${measurementType}. ` +
                        'Forneça um experiment com referência de calibração ou uma referência customizada.'
                );
            }
            referenceReading = {};
            for (const key in rawData) {
                if (Array.isArray(rawData[key]) && rawData[key].length > 0) {
                    const index = Math.min(
                        customReferenceIndex,
                        rawData[key].length - 1
                    );
                    referenceReading[key] = rawData[key][index];
                } else {
                    referenceReading[key] = rawData[key];
                }
            }
        }
    }

    // Normaliza nulls → 0
    for (const k of Object.keys(referenceReading)) {
        if (referenceReading[k] == null) referenceReading[k] = 0;
    }

    // --- Filtra canais válidos por sensor (remove campos que não pertencem ao sensor) ---
    const VALID_CHANNELS = {
        AS7341: [
            'f1',
            'f2',
            'f3',
            'f4',
            'f5',
            'f6',
            'f7',
            'f8',
            'clear',
            'nir',
            'gain',
            'integrationTime',
        ],
        APDS9960: [
            'r',
            'g',
            'b',
            'c',
            'red',
            'green',
            'blue',
            'clear',
            'gain',
            'integrationTime',
        ],
    };
    const validKeys = VALID_CHANNELS[sensorType];
    if (validKeys) {
        for (const obj of [rawData, referenceReading]) {
            if (!obj || typeof obj !== 'object') continue;
            for (const key of Object.keys(obj)) {
                if (!validKeys.includes(key)) {
                    delete obj[key];
                }
            }
        }
    }

    // APDS9960: API espera r, g, b, c (renomeia long → short)
    if (sensorType === 'APDS9960') {
        const longToShort = { red: 'r', green: 'g', blue: 'b', clear: 'c' };
        for (const obj of [rawData, referenceReading]) {
            if (!obj || typeof obj !== 'object') continue;
            for (const [long, short] of Object.entries(longToShort)) {
                if (long in obj && !(short in obj)) {
                    obj[short] = obj[long];
                    delete obj[long];
                }
            }
        }
    }

    // Preenche gain/integrationTime do rawData a partir do experiment (OBRIGATÓRIOS na API)
    const expMeasurement = experiment?.spectral?.[measurementType];
    if (!gainFromChart) {
        const g = expMeasurement?.gain;
        if (g != null) rawData.gain = g;
    }
    if (!intTimeFromChart) {
        const it = expMeasurement?.integrationTime ?? expMeasurement?.timeMs;
        if (it != null) rawData.integrationTime = it;
    }

    // gain e integrationTime são obrigatórios na API — erro se ausentes
    if (rawData.gain == null) {
        throw new Error(
            `gain não disponível para rawData (${sensorType}/${measurementType}). ` +
                'Verifique os dados do gráfico ou o experiment.'
        );
    }
    if (rawData.integrationTime == null) {
        throw new Error(
            `integrationTime não disponível para rawData (${sensorType}/${measurementType}). ` +
                'Verifique os dados do gráfico ou o experiment.'
        );
    }

    // Garante gain/integrationTime no referenceReading (obrigatórios na API)
    if (referenceReading.gain == null) {
        referenceReading.gain = rawData.gain;
    }
    if (referenceReading.integrationTime == null) {
        referenceReading.integrationTime = rawData.integrationTime;
    }

    return {
        sensorType,
        rawData,
        targetColorSpaces: apiConfig.targetColorSpaces,
        calibration: {
            ...apiConfig.calibration,
            referenceReading,
        },
        ...(apiConfig.series ? { series: apiConfig.series } : {}),
        output: apiConfig.output,
    };
}
